import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

import { api, formatApiError } from "@/lib/api";
import ReportPageShell from "@/components/reports/ReportPageShell";
import DateRangePicker, { rangeToParams } from "@/components/reports/DateRangePicker";
import ExportDropdown from "@/components/reports/ExportDropdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import { useWithBack } from "@/lib/nav";
import StatusBadge from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";

const CHART_COLOR = "hsl(var(--primary))";

export default function CustomerHistoryReport() {
  const withBackHref = useWithBack();
  const [range, setRange] = useState({ preset: "ytd", from: null, to: null });
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    api.get("/customers", { params: { active: true, limit: 500 } })
      .then(({ data }) => setCustomers(data.items || []))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const load = useCallback(async () => {
    if (!customerId) { setData(null); return; }
    const my = ++reqIdRef.current;
    setLoading(true);
    try {
      const { data } = await api.get("/reports/customer-history", {
        params: { customer_id: customerId, ...rangeToParams(range) },
      });
      if (my !== reqIdRef.current) return;
      setData(data);
    } catch (err) {
      if (my !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (my === reqIdRef.current) setLoading(false);
    }
  }, [customerId, range]);

  useEffect(() => { load(); }, [load]);

  const chart = data ? (
    <div style={{ width: "100%", height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={data.per_month || []} margin={{ left: 10, right: 20, top: 10, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
          <Bar dataKey="count" fill={CHART_COLOR} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  ) : null;

  return (
    <ReportPageShell
      testId="customer-history-report"
      title="Customer Service History"
      subtitle="All jobs for a single customer across a date range."
      headerActions={customerId ? (
        <ExportDropdown
          basePath="/reports/customer-history"
          params={{ customer_id: customerId, ...rangeToParams(range) }}
          filename={`customer-history-${customerId}`}
          testId="customer-history-export"
        />
      ) : null}
      filters={
        <>
          <div className="w-64">
            <EntityCombobox
              items={customers}
              value={customerId || ""}
              onChange={(v) => setCustomerId(v)}
              placeholder="Pick a customer"
              emptyMessage="No customers match."
              testid="customer-history-customer"
              triggerClassName="h-8 text-xs"
            />
          </div>
          <DateRangePicker value={range} onChange={setRange} testId="customer-history-range" />
          {data && (
            <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
              <span data-testid="customer-history-total-jobs"><strong className="text-foreground">{data.total_jobs}</strong> jobs</span>
              <span data-testid="customer-history-total-hours"><strong className="text-foreground">{data.total_time_on_site_hours}</strong> h on site</span>
              {data.last_serviced_at && <span>Last: <strong className="text-foreground">{formatDate(data.last_serviced_at)}</strong></span>}
            </div>
          )}
        </>
      }
      chartTitle={data ? "Jobs per month" : null}
      chart={chart}
      table={
        !customerId ? (
          <div className="py-8 text-center text-xs text-muted-foreground" data-testid="customer-history-prompt">
            Pick a customer to load their service history.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="customer-history-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Job</th>
                <th className="text-left py-2 px-3">Machines</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Completed</th>
                <th className="text-left py-2 px-3">Engineer</th>
                <th className="text-right py-2 px-3">Time (min)</th>
                <th className="text-left py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : !data || data.jobs.length === 0 ? (
                <tr><td colSpan={7} className="py-8 text-center text-xs text-muted-foreground">No jobs in range.</td></tr>
              ) : data.jobs.map((j) => (
                <tr key={j.id} data-testid={`customer-history-row-${j.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="py-2 px-3"><Link to={withBackHref(`/jobs/${j.id}`)} className="font-mono text-xs font-medium hover:text-primary" data-testid={`customer-history-job-link-${j.id}`}>{j.job_number}</Link></td>
                  <td className="py-2 px-3 text-muted-foreground text-xs truncate max-w-[280px]" title={j.machine_summary}>{j.machine_summary}</td>
                  <td className="py-2 px-3"><StatusBadge tone={j.status === "closed" ? "success" : j.status === "aborted" ? "danger" : "info"}>{j.status}</StatusBadge></td>
                  <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{formatDate(j.completed_at)}</td>
                  <td className="py-2 px-3 text-muted-foreground">{j.engineer || "—"}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{j.time_on_site_minutes}</td>
                  <td className="py-2 px-3">{j.actions_required && <StatusBadge tone="warning">Flagged</StatusBadge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    />
  );
}
